import { ExternalLink, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { MaskedLines, FadeUp, SectionTag } from "@/components/Reveal";
import { PARTNERS } from "@/data/content";

const PartnerCard = ({ p, id, showOrt = true }) => (
  <FadeUp>
    <article data-testid={id} className="group overflow-hidden border border-sand bg-creme">
      <div className="overflow-hidden">
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          className="aspect-[16/10] w-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-[1.05]"
        />
      </div>
      <div className="p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-2xl">{p.name}</h3>
            {showOrt && p.ort && (
              <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-rust">
                <MapPin size={12} /> {p.ort}
              </p>
            )}
            {p.kategorie && (
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-rust">{p.kategorie}</p>
            )}
          </div>
          <a
            href={p.link}
            data-testid={`${id}-link`}
            aria-label={`Website von ${p.name} öffnen`}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-sand text-stone transition-colors hover:border-moss hover:bg-moss hover:text-creme"
          >
            <ExternalLink size={15} />
          </a>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-stone">{p.description}</p>
        {p.besonderheiten && (
          <p className="mt-4 border-t border-sand/70 pt-4 text-xs text-stone">
            <span className="font-semibold text-ink">Besonders: </span>{p.besonderheiten}
          </p>
        )}
      </div>
    </article>
  </FadeUp>
);

const Partner = () => (
  <div data-testid="partner-page">
    <SEO
      title="Partner – Locations, Catering & Dienstleister | Daviz & Puxi"
      description="Unsere Partner für Ihre Zeremonie: ausgewählte Locations, Catering, Floristik, Fotografie, Technik und Musik – Menschen, denen wir vertrauen."
      path="/partner"
      noindex
    />

    <section className="mx-auto max-w-7xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
      <FadeUp>
        <SectionTag>Partner</SectionTag>
      </FadeUp>
      <MaskedLines
        lines={["Gute Zeremonien", "sind Teamarbeit."]}
        className="mt-6 font-serif text-5xl leading-[1.05] sm:text-7xl"
        delay={0.15}
      />
      <FadeUp delay={0.5}>
        <p className="mt-8 max-w-2xl text-lg leading-relaxed text-stone">
          Wir arbeiten mit Locations, Caterern und Dienstleistern zusammen, die wir persönlich
          kennen und schätzen. Keine Werbung, keine Provisionen – nur Empfehlungen, hinter denen
          wir stehen.
        </p>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
      <FadeUp className="mb-10">
        <h2 className="font-serif text-3xl sm:text-4xl">Locations</h2>
        <p className="mt-2 max-w-xl text-stone">Orte mit Charakter – vom Gutshof bis zum Seeufer.</p>
      </FadeUp>
      <div className="grid gap-7 md:grid-cols-2 lg:grid-cols-3">
        {PARTNERS.locations.map((p, i) => (
          <PartnerCard key={p.name} p={p} id={`partner-location-${i}`} />
        ))}
      </div>

      <FadeUp className="mb-10 mt-24">
        <h2 className="font-serif text-3xl sm:text-4xl">Catering</h2>
        <p className="mt-2 max-w-xl text-stone">Küche, die nach Feier schmeckt – ehrlich und regional.</p>
      </FadeUp>
      <div className="grid gap-7 md:grid-cols-2">
        {PARTNERS.catering.map((p, i) => (
          <PartnerCard key={p.name} p={p} id={`partner-catering-${i}`} />
        ))}
      </div>

      <FadeUp className="mb-10 mt-24">
        <h2 className="font-serif text-3xl sm:text-4xl">Ausstattung & weitere Dienstleister</h2>
        <p className="mt-2 max-w-xl text-stone">
          Floristik, Fotografie, Technik, Musik – alles, was Ihre Zeremonie rund macht.
        </p>
      </FadeUp>
      <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
        {PARTNERS.dienstleister.map((p, i) => (
          <PartnerCard key={p.name} p={p} id={`partner-dienstleister-${i}`} showOrt={false} />
        ))}
      </div>

      <FadeUp className="mt-20 rounded-2xl border border-dashed border-sand bg-parch/50 p-10 text-center">
        <p className="font-serif text-2xl">Sie sind Dienstleister und passen zu uns?</p>
        <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-stone">
          Wir erweitern unser Netzwerk laufend – mit Menschen, die Qualität und Persönlichkeit
          teilen. Schreiben Sie uns, wir lernen uns gern kennen.
        </p>
      </FadeUp>
    </section>

    <CTASection
      title={["Sie suchen noch", "den passenden Ort?"]}
      text="Wir beraten Sie gern – unverbindlich und ehrlich. Oft entsteht aus einem Gespräch über die Location schon das ganze Konzept."
      buttonLabel="Beratung anfragen"
      subject="Beratung: Location & Partner"
    />
  </div>
);

export default Partner;
